import { logger } from '@/lib/logger';

/**
 * Password Encryption Utilities
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  TEMPORARY MEASURE - REMOVE WHEN HTTPS IS ENABLED IN PRODUCTION           ║
 * ╠═══════════════════════════════════════════════════════════════════════════╣
 * ║  This encryption exists because the dev/staging environment uses HTTP.    ║
 * ║  Without HTTPS, passwords would be transmitted in plaintext.              ║
 * ║                                                                           ║
 * ║  PRODUCTION TODO:                                                         ║
 * ║  1. Enable HTTPS/TLS on all environments                                  ║
 * ║  2. Remove NEXT_PUBLIC_ENCRYPTION_KEY from environment                    ║
 * ║  3. Update encryptPassword() to return password as-is                     ║
 * ║  4. Rely on HTTPS for transport security                                  ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * SECURITY NOTES:
 * - Encryption key MUST be set via NEXT_PUBLIC_ENCRYPTION_KEY environment variable
 * - NO hardcoded fallback key (previous vulnerability fixed)
 * - This is defense-in-depth for HTTP environments, NOT a replacement for HTTPS
 * - Backend must have matching decryption with the same key
 */

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * Get encryption key from environment
 * SECURITY: No hardcoded fallback - must be explicitly configured
 */
const getEncryptionKey = (): string | null => {
  const key = process.env.NEXT_PUBLIC_ENCRYPTION_KEY;

  if (!key) {
    logger.warn(
      '[SECURITY] NEXT_PUBLIC_ENCRYPTION_KEY not configured. ' +
      'Password will be sent without additional encryption. ' +
      'This is ONLY acceptable if using HTTPS.'
    );
    return null;
  }

  // Validate key length (should be at least 32 characters)
  if (key.length < 32) {
    logger.error(
      '[SECURITY] NEXT_PUBLIC_ENCRYPTION_KEY is too short (< 32 chars). ' +
      'Use a strong key: openssl rand -hex 32'
    );
  }

  return key;
};

// ============================================================================
// PASSWORD VALIDATION
// ============================================================================

/**
 * Validate password meets minimum security requirements
 *
 * @param password - Password to validate
 * @returns Object with isValid boolean and any error messages
 */
export const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!password) {
    errors.push('Password is required');
    return { isValid: false, errors };
  }

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters');
  }

  if (password.length > 128) {
    errors.push('Password must be less than 128 characters');
  }

  // Check for common weak passwords
  const commonPasswords = ['password', '12345678', 'qwerty123', 'letmein', 'welcome'];
  if (commonPasswords.includes(password.toLowerCase())) {
    errors.push('Password is too common');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Check password strength
 *
 * @param password - Password to check
 * @returns Strength score (0-4) and description
 */
export const checkPasswordStrength = (password: string): { score: number; description: string } => {
  if (!password) return { score: 0, description: 'No password' };

  let score = 0;

  // Length
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (password.length >= 16) score++;

  // Complexity
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  // Cap at 4
  score = Math.min(score, 4);

  const descriptions = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'];
  return { score, description: descriptions[score] };
};

// ============================================================================
// PASSWORD ENCRYPTION
// ============================================================================

/**
 * Encrypts a password using AES encryption with PBKDF2 key derivation
 * This matches the backend's decrypt_password function in Utility/helper.py
 *
 * ⚠️  TEMPORARY: This encryption is for HTTP environments only.
 *     Remove when HTTPS is enabled in production.
 *
 * Algorithm:
 * - PBKDF2 key derivation (1000 iterations, 256-bit key)
 * - AES encryption in CBC mode
 * - PKCS7 padding
 * - Format: salt (16 bytes) + iv (16 bytes) + ciphertext
 * - Base64 encoded
 *
 * @param password - Plain text password to encrypt
 * @returns Encrypted password (base64) or plain password if no key configured
 */
export const encryptPassword = async (password: string): Promise<string> => {
  const encryptionKey = getEncryptionKey();

  // If no encryption key, return password as-is (relies on HTTPS)
  if (!encryptionKey) {
    return password;
  }

  // WS-5c: crypto-js is ~160KB minified and was a STATIC import, so it shipped
  // on every page load — including to the large majority of deployments where
  // NEXT_PUBLIC_ENCRYPTION_KEY is unset and the branch above returns early, so
  // it never even ran. Loading it here means it is fetched only when a key is
  // actually configured. That is why this function is async; the sole caller
  // (AuthService.login) was already async.
  const { default: CryptoJS } = await import('crypto-js');

  try {
    // Generate random salt (16 bytes)
    const salt = CryptoJS.lib.WordArray.random(16);

    // Derive encryption key using PBKDF2
    const derivedKey = CryptoJS.PBKDF2(encryptionKey, salt, {
      keySize: 256 / 32, // 256 bits = 32 bytes = 8 words (32-bit each)
      iterations: 1000,
    });

    // Generate random IV (16 bytes)
    const iv = CryptoJS.lib.WordArray.random(16);

    // Encrypt password using AES
    const cipherText = CryptoJS.AES.encrypt(password, derivedKey, {
      iv: iv,
      padding: CryptoJS.pad.Pkcs7,
    });

    // Combine salt + iv + ciphertext to send to the server
    const combined = salt.concat(iv).concat(cipherText.ciphertext);

    return combined.toString(CryptoJS.enc.Base64);
  } catch (error) {
    logger.error('[SECURITY] Password encryption failed:', error);
    // Fail safely - return password (will work if HTTPS is enabled)
    return password;
  }
};

/**
 * Check if password encryption is enabled
 * @returns true if NEXT_PUBLIC_ENCRYPTION_KEY is configured
 */
export const isEncryptionEnabled = (): boolean => {
  return !!process.env.NEXT_PUBLIC_ENCRYPTION_KEY;
};

